import { useState } from 'react';
import { FolderKanban } from 'lucide-react';
import { Drawer } from '@/shared/components/ui/Drawer';
import { Button } from '@/shared/components/ui/Button';
import { Select, Input, MultiSelect } from '@/shared/components/ui';
import { Badge } from '@/shared/components/ui/Badge';
import type { Task, TaskStatus, TaskPriority, UpdateTaskPayload, ProjectOption, UserOption } from '../types';

const STATUS_OPTIONS = [
  { value: 'todo', label: 'To Do' },
  { value: 'in_progress', label: 'In Progress' },
  { value: 'review', label: 'Review' },
  { value: 'done', label: 'Done' },
];

const PRIORITY_OPTIONS = [
  { value: 'low', label: 'Low' },
  { value: 'medium', label: 'Medium' },
  { value: 'high', label: 'High' },
  { value: 'critical', label: 'Critical' },
];

const PRIORITY_TONE: Record<string, 'neutral' | 'success' | 'warning' | 'danger' | 'info'> = {
  low: 'neutral',
  medium: 'info',
  high: 'warning',
  critical: 'danger',
};

interface TaskDrawerProps {
  task: Task | null;
  projects: ProjectOption[];
  users: UserOption[];
  isAdmin: boolean;
  saving?: boolean;
  onClose: () => void;
  onSave: (id: string, payload: UpdateTaskPayload) => void;
}

type DrawerForm = {
  title: string;
  description: string;
  requirements: string;
  project_id: string;
  assigned_to: string[];
  status: TaskStatus;
  priority: TaskPriority;
  due_date: string;
  due_time: string;
  client_name: string;
  client_phone: string;
};

function toForm(task: Task): DrawerForm {
  return {
    title: task.title ?? '',
    description: task.description ?? '',
    requirements: task.requirements ?? '',
    project_id: task.project_id ?? '',
    assigned_to: task.assignees?.map((a) => a.id) ?? [],
    status: task.status ?? 'todo',
    priority: task.priority ?? 'medium',
    due_date: task.due_date ?? '',
    due_time: task.due_time ?? '',
    client_name: task.client_name ?? '',
    client_phone: task.client_phone ?? '',
  };
}

export function TaskDrawer({ task, projects, users, isAdmin, saving, onClose, onSave }: TaskDrawerProps) {
  const [form, setForm] = useState<DrawerForm | null>(null);

  // Sync the editable form whenever the selected task changes, adjusting state
  // during render (via previous-value tracking) instead of in an effect.
  const [prevTask, setPrevTask] = useState(task);
  if (task !== prevTask) {
    setPrevTask(task);
    if (task) setForm(toForm(task));
    else setForm(null);
  }

  if (!task || !form) return null;

  const set = <K extends keyof DrawerForm>(key: K, value: DrawerForm[K]) =>
    setForm((prev) => (prev ? { ...prev, [key]: value } : prev));

  const handleSave = (e: React.FormEvent) => {
    e.preventDefault();
    if (!form) return;
    const payload: UpdateTaskPayload = {
      title: form.title.trim(),
      description: form.description.trim(),
      requirements: form.requirements.trim(),
      project_id: form.project_id || null,
      assigned_to: form.assigned_to,
      status: form.status,
      priority: form.priority,
      due_date: form.due_date || null,
      due_time: form.due_time || undefined,
      client_name: form.client_name.trim(),
      client_phone: form.client_phone.trim(),
    };
    onSave(task.id, payload);
  };

  const projectOptions = [
    { value: '', label: 'No Project' },
    ...projects.map((p) => ({ value: p.id, label: p.name })),
  ];

  return (
    <Drawer open={!!task} onClose={onClose} title={form.title || 'Task Details'} width="w-[520px]">
      <form onSubmit={handleSave} className="flex h-full flex-col gap-5">
        {/* Header meta */}
        <div className="flex flex-wrap gap-2">
          <Badge tone={PRIORITY_TONE[form.priority] ?? 'neutral'}>
            {form.priority.toUpperCase()}
          </Badge>
          <span className="inline-flex items-center gap-1 rounded-card-sm bg-surface-variant px-2 py-0.5 text-body-sm text-on-surface-variant select-all">
            ID: {task.id}
          </span>
          {task.project_name && (
            <span className="inline-flex items-center gap-1 rounded-card-sm bg-surface-variant px-2 py-0.5 text-body-sm text-on-surface-variant">
              <FolderKanban className="h-3.5 w-3.5" /> {task.project_name}
            </span>
          )}
        </div>

        {/* Title */}
        <Input
          label="Title"
          name="drawer-task-title"
          value={form.title}
          onChange={(e) => set('title', e.target.value)}
          required
        />

        {/* Status + Priority */}
        <div className="grid grid-cols-2 gap-3">
          <Select
            label="Status"
            name="drawer-task-status"
            value={form.status}
            onChange={(e) => set('status', e.target.value as TaskStatus)}
            options={STATUS_OPTIONS}
          />
          <Select
            label="Priority"
            name="drawer-task-priority"
            value={form.priority}
            onChange={(e) => set('priority', e.target.value as TaskPriority)}
            options={PRIORITY_OPTIONS}
          />
        </div>

        {/* Project + Due Date */}
        <div className="grid grid-cols-2 gap-3">
          <Select
            label="Project"
            name="drawer-task-project"
            value={form.project_id}
            onChange={(e) => set('project_id', e.target.value)}
            options={projectOptions}
          />
          <Input
            label="Due Date"
            name="drawer-task-due"
            type="date"
            value={form.due_date}
            onChange={(e) => set('due_date', e.target.value)}
          />
        </div>

        {/* Due Time + Client Info */}
        <div className="grid grid-cols-2 gap-3">
          <Input
            label="Due Time"
            name="drawer-task-due-time"
            type="time"
            value={form.due_time}
            onChange={(e) => set('due_time', e.target.value)}
          />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Input
            label="Client Name"
            name="drawer-task-client-name"
            value={form.client_name}
            onChange={(e) => set('client_name', e.target.value)}
            placeholder="John Doe..."
          />
          <Input
            label="Client Phone"
            name="drawer-task-client-phone"
            value={form.client_phone}
            onChange={(e) => set('client_phone', e.target.value)}
            placeholder="+1234567890"
          />
        </div>

        {/* Assignees (admin only) */}
        {isAdmin && (
          <MultiSelect
            label="Assign To"
            options={users.map((u) => ({ value: u.id, label: u.display_name }))}
            value={form.assigned_to}
            onChange={(val) => set('assigned_to', val)}
            placeholder="Select assignees..."
          />
        )}

        {/* Description */}
        <div>
          <label htmlFor="drawer-task-desc" className="mb-1.5 block text-body-sm font-medium text-on-surface">
            Description
          </label>
          <textarea
            id="drawer-task-desc"
            rows={3}
            value={form.description}
            onChange={(e) => set('description', e.target.value)}
            className="w-full resize-none rounded border border-outline bg-surface px-3 py-2 text-data-md text-on-surface placeholder:text-on-surface-variant/70 focus:outline-none focus:ring-2 focus:ring-primary/40"
          />
        </div>

        {/* Requirements */}
        <div>
          <label htmlFor="drawer-task-reqs" className="mb-1.5 block text-body-sm font-medium text-on-surface">
            Requirements
          </label>
          <textarea
            id="drawer-task-reqs"
            rows={2}
            value={form.requirements}
            onChange={(e) => set('requirements', e.target.value)}
            className="w-full resize-none rounded border border-outline bg-surface px-3 py-2 text-data-md text-on-surface placeholder:text-on-surface-variant/70 focus:outline-none focus:ring-2 focus:ring-primary/40"
          />
        </div>

        {/* Footer */}
        <div className="mt-auto flex justify-end gap-3 border-t border-outline pt-4">
          <Button variant="outline" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button type="submit" loading={saving}>
            Save Changes
          </Button>
        </div>
      </form>
    </Drawer>
  );
}
